import { useMemo, useState } from 'react';
import { Link } from 'react-router-dom';

import { DataTable, Pager, StatusDot, TableToolbar } from '@/components/DataTable';
import { NewAppModal } from '@/components/NewAppModal';
import {
  APPS_PER_PAGE,
  useDebounced,
  type AppStatusFilter,
  languageCodes,
  languageDetail,
  platformLabel,
  priceLabel,
  useAdminApps,
  type AdminAppRow,
} from '@/lib/apps';
import { useHeaderActions } from '@/lib/editor-bar';

const GRID = 'minmax(0, 2fr) 1.2fr 0.8fr 0.9fr 88px';

/** One line of explanation, centred in the table, for every state that is not rows. */
function TableNotice({ children }: { children: React.ReactNode }) {
  return (
    <p className="m-0 px-[22px] py-10 text-center text-[13.5px]" style={{ color: 'var(--muted)' }}>
      {children}
    </p>
  );
}

function AppRow({ app }: { app: AdminAppRow }) {
  const codes = languageCodes(app.locales);
  return (
    <div
      className="grid items-center border-b"
      style={{ gridTemplateColumns: '1fr', borderColor: 'var(--line)' }}
    >
      <Link
        // By id: the slug is editable, so a link keyed on it breaks on rename.
        to={`/apps/${app.id}`}
        className="row-link focus-ring grid items-center gap-3 py-2 pl-3.5"
        style={{ gridTemplateColumns: GRID }}
      >
        <span className="flex min-w-0 items-center gap-2.5">
          {/*
            The app's icon, and for a long time a decoration that could never
            be one: this was an empty box with no `img` in it and no field
            behind it, so an app with artwork looked exactly like an app
            without. The row had a picture-shaped hole and nothing to put in it,
            because `AdminAppRow` carried no icon at all.

            `alt=""`: the app's name is the next thing in the row, and a screen
            reader announcing it twice is worse than not announcing the picture.
          */}
          {app.iconUrl ? (
            <img
              src={app.iconUrl}
              alt=""
              className="h-[26px] w-[26px] flex-none rounded-[7px] border object-cover"
              style={{ borderColor: 'var(--line2)' }}
            />
          ) : (
            <span
              className="h-[26px] w-[26px] flex-none rounded-[7px] border"
              style={{ background: 'var(--in)', borderColor: 'var(--line2)' }}
            />
          )}
          <span className="truncate text-[13px] font-medium">{app.name}</span>
          <span className="truncate font-mono text-[10.5px]" style={{ color: 'var(--dim)' }}>
            {app.slug}
          </span>
          {/*
            An app made before Apple IDs were required has no figures and no
            store link until somebody fills the number in — worth a word in
            the row, since nothing else about it looks wrong.
          */}
          {app.appleId === null ? (
            <span
              className="flex-none rounded-[6px] border px-1.5 py-0.5 font-mono text-[9.5px] tracking-[0.05em] uppercase"
              style={{ color: 'var(--danger)', borderColor: 'var(--line2)' }}
              title="No Apple ID: not linked to the App Store"
            >
              Not linked
            </span>
          ) : null}
        </span>
        <span className="truncate text-xs" style={{ color: 'var(--muted)' }}>
          {platformLabel(app.platforms)}
        </span>
        <span className="text-[12.5px]">{priceLabel(app.price)}</span>
        <span
          className="font-mono text-[10.5px]"
          style={{ color: 'var(--dim)' }}
          title={languageDetail(app.locales)}
        >
          {codes.length ? codes.join(' ') : '—'}
        </span>
        <StatusDot
          status={app.discontinuedAt ? 'Discontinued' : app.published ? 'Published' : 'Draft'}
        />
      </Link>
    </div>
  );
}

export default function AppsPage() {
  const [search, setSearch] = useState('');
  const [creating, setCreating] = useState(false);
  /*
   * Discontinued apps are hidden until asked for. They are not a third kind of
   * draft — they are finished, and listing them beside work that is waiting
   * would make the table say the wrong thing about both.
   */
  const [status, setStatus] = useState<AppStatusFilter | ''>('');
  /*
   * The box updates as fast as somebody types; the query waits for them to
   * stop. Querying per keystroke sent six requests for a six-letter word and
   * spent the rate limit on the way to the answer.
   */
  const [page, setPage] = useState(1);
  const debouncedSearch = useDebounced(search);

  /*
   * Back to page 1 whenever the filters change. Page 4 of an unfiltered
   * catalogue is past the end of most searches, and a table answering a new
   * search with "no apps" because it is still on page 4 tells somebody their
   * search failed when it did not.
   *
   * ⚠️ Adjusted **during render**, not in an effect. React runs effects after
   * the render commits, so the query below would already have been asked with
   * the new search and the *old* page — a request nobody wanted, thrown away a
   * moment later, against a rate limit of five in the window. That is the very
   * thing the debounce beside it exists to protect. Setting state during render
   * of the same component is the pattern React documents for exactly this: it
   * throws this render away and re-runs the component with `page` already 1,
   * before anything is committed and therefore before `useQuery` subscribes.
   * So `page` below can be read plainly — the render that could have sent the
   * stale request is the one being discarded.
   */
  const [filteredBy, setFilteredBy] = useState({ search: debouncedSearch, status });
  if (filteredBy.search !== debouncedSearch || filteredBy.status !== status) {
    setFilteredBy({ search: debouncedSearch, status });
    setPage(1);
  }

  const apps = useAdminApps({ search: debouncedSearch, status: status || undefined, page });

  /*
   * ⚠️ And back onto a page that exists when the catalogue shrinks under one.
   *
   * A dead screen somebody reaches by doing nothing wrong: 51 apps, on page 2,
   * discontinue one. The total is 50, so page 2 is gone — the pager draws
   * nothing, because one page needs no pager; the rows are empty; and the table
   * says "No apps yet." over a catalogue of fifty, with no control left to
   * press. The same on any refetch that removes rows: a delete, a filter
   * somebody else's save narrowed.
   *
   * During render, like the reset above, so the request for the page that is
   * gone is never made.
   */
  const pages = Math.max(1, Math.ceil((apps.data?.total ?? 0) / APPS_PER_PAGE));
  if (apps.data && page > pages) setPage(pages);

  /*
   * Rearranging is only offered on the unfiltered list, and that is not a
   * shortcut. "Move up" means swap with the row above — and when rows are
   * hidden, the row above on screen is not the row above in the catalogue. The
   * app would jump past something invisible, or appear not to move at all.
   */
  /*
   * Published in the header: v2 puts the page's one action where the title
   * used to be. Memoised, because the header compares what it is handed and a
   * fresh array every render would re-run the effect that publishes it.
   */
  const actions = useMemo(
    () => [
      {
        label: 'New app',
        onClick: () => setCreating(true),
        kind: 'accent' as const,
        icon: 'plus' as const,
      },
    ],
    [],
  );
  useHeaderActions(actions);

  const rows = apps.data?.items ?? [];
  const total = apps.data?.total ?? 0;

  /*
   * Both numbers come from the server, over every page rather than this one.
   *
   * ⚠️ They used to be counted from the rows in hand, which was right while
   * every row was in hand. Left that way, "12 items · 5 published" would
   * silently start meaning "on this page" — the same sentence, in the same
   * place, quietly answering a narrower question. A figure like that is worse
   * than no figure, because nothing about it looks wrong.
   */
  const meta = apps.data
    ? `${total} ${total === 1 ? 'item' : 'items'} · ${apps.data.published} published`
    : '';

  return (
    <>
      <section className="px-[18px] pt-3.5">
        <TableToolbar
          label="Search apps"
          value={search}
          onChange={setSearch}
          placeholder="Filter apps…"
          meta={meta}
        >
          <label className="block">
            <span className="sr-only">Filter by status</span>
            <select
              value={status}
              onChange={(event) => setStatus(event.target.value as AppStatusFilter | '')}
              className="field field-filter focus-ring"
            >
              <option value="">All</option>
              <option value="PUBLISHED">Published</option>
              <option value="DRAFT">Draft</option>
              <option value="DISCONTINUED">Discontinued</option>
            </select>
          </label>
        </TableToolbar>

        {/*
          Says how the table is ordered. Not obvious from a table, and it is
          the order the website uses too — which is the part somebody actually
          needs to know before wondering why an app is where it is.
        */}
        <p className="mt-0 mb-2.5 text-[11.5px]" style={{ color: 'var(--muted)' }}>
          Newest first. This is the order apps appear on the website.
        </p>

        <DataTable
          gridTemplate={GRID}
          columns={['App', 'Platforms', 'Price', 'Languages', 'Status']}
        >
          {apps.isPending ? <TableNotice>Loading apps…</TableNotice> : null}

          {apps.isError ? (
            <TableNotice>
              Could not load the apps.{' '}
              <button
                type="button"
                onClick={() => void apps.refetch()}
                className="prose-link focus-ring cursor-pointer"
                style={{ color: 'var(--accent)' }}
              >
                Try again
              </button>
            </TableNotice>
          ) : null}

          {/*
            Two different empty states, because they call for two different
            actions: nothing exists yet, or nothing matches what was typed.
            One message for both would send somebody to create an app they
            already have.
          */}
          {/*
            Named from the *debounced* search, not the box: that is the question
            the server answered. Reading the box names a search that has not
            been run — and with `keepPreviousData` the rows underneath still
            belong to the previous one.
          */}
          {apps.data?.items.length === 0 ? (
            <TableNotice>
              {debouncedSearch.trim()
                ? `No app matches “${debouncedSearch.trim()}”.`
                : status === 'DISCONTINUED'
                  ? 'No apps have been discontinued.'
                  : 'No apps yet.'}
            </TableNotice>
          ) : null}

          {rows.map((app) => (
            <AppRow key={app.id} app={app} />
          ))}
        </DataTable>
        <Pager page={page} perPage={APPS_PER_PAGE} total={total} onPage={setPage} noun="app" />
      </section>
      <div className="h-10" />
      {creating ? <NewAppModal onClose={() => setCreating(false)} /> : null}
    </>
  );
}
